import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

export interface SignaturePadProps {
  /** Label above the pad */
  label: string;
  /** Current signature (PNG data URL) or null; drives the helper text */
  value: string | null;
  /** Called with a PNG data URL after each stroke, or null when cleared / resized */
  onChange: (dataUrl: string | null) => void;
  /** Re-measure when the pad becomes visible (e.g. a sheet's open flag). Default true. */
  active?: boolean;
  /** Canvas height in px (default 180) */
  height?: number;
  disabled?: boolean;
}

type PointGroups = ReturnType<SignatureCanvas['toData']>;

/** Stretch the stroke x positions to a new canvas width (the height never changes). */
export const scalePointGroups = (groups: PointGroups, fromWidth: number, toWidth: number): PointGroups => {
  const k = fromWidth > 0 ? toWidth / fromWidth : 1;
  return groups.map((g) => ({ ...g, points: g.points.map((p) => ({ ...p, x: p.x * k })) }));
};

/**
 * Finger / stylus / mouse signature pad. The canvas takes the real width of its box (no CSS
 * stretching), so strokes land under the finger. A resize (rotation, keyboard) redraws the
 * strokes scaled to the new width (Phase 18 M4) and reports the new image; with no strokes the
 * parent is told there is no signature. Returns a PNG data URL ('data:image/png;base64,...').
 */
const SignaturePad: React.FC<SignaturePadProps> = ({ label, value, onChange, active = true, height = 180, disabled = false }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<SignatureCanvas>(null);
  const [width, setWidth] = useState(320);
  /** The strokes and the width they were drawn at, kept across the canvas remount on resize */
  const strokesRef = useRef<{ groups: PointGroups; width: number } | null>(null);

  useEffect(() => {
    if (!active || !boxRef.current) return undefined;
    const el = boxRef.current;
    const measure = () => setWidth(Math.max(240, Math.floor(el.clientWidth)));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);

  // Resizing remounts the canvas (blank): redraw the kept strokes, else tell the parent it is empty
  useEffect(() => {
    const c = canvasRef.current;
    const kept = strokesRef.current;
    if (c && kept?.groups.length && typeof c.fromData === 'function') {
      try {
        const groups = scalePointGroups(kept.groups, kept.width, width);
        c.fromData(groups);
        strokesRef.current = { groups, width };
        onChange(c.getCanvas().toDataURL('image/png'));
        return;
      } catch {
        strokesRef.current = null;
      }
    }
    onChange(null);
  }, [width]); // eslint-disable-line react-hooks/exhaustive-deps

  // The parent cleared the signature (e.g. a new visit): clear the strokes too
  useEffect(() => {
    if (value || !strokesRef.current) return;
    strokesRef.current = null;
    canvasRef.current?.clear();
  }, [value]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    if (disabled) c.off(); else c.on();
  }, [disabled, width]);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <button
          type="button"
          className="min-h-[40px] px-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          disabled={disabled}
          onClick={() => { strokesRef.current = null; canvasRef.current?.clear(); onChange(null); }}
        >
          Clear
        </button>
      </div>
      <div ref={boxRef} className="overflow-hidden rounded-lg border-2 border-gray-300 bg-gray-50">
        <SignatureCanvas
          key={width}
          ref={canvasRef}
          penColor="black"
          canvasProps={{ width, height, className: 'block touch-none', 'aria-label': 'Signature pad' } as React.CanvasHTMLAttributes<HTMLCanvasElement>}
          onEnd={() => {
            const c = canvasRef.current;
            if (!c || c.isEmpty()) return;
            if (typeof c.toData === 'function') strokesRef.current = { groups: c.toData(), width };
            onChange(c.getCanvas().toDataURL('image/png'));
          }}
        />
      </div>
      <p className="mt-1 text-xs text-gray-500">{value ? 'Signature captured' : 'Sign inside the box'}</p>
    </div>
  );
};

export default SignaturePad;
